import React, { memo, useMemo, lazy, Suspense, useEffect } from 'react'
import { Loading } from 'components/shared'
import { useSelector } from 'react-redux'
import {
    LAYOUT_TYPE_CLASSIC,
    LAYOUT_TYPE_MODERN,
    LAYOUT_TYPE_SIMPLE,
    LAYOUT_TYPE_STACKED_SIDE,
    LAYOUT_TYPE_DECKED,
    LAYOUT_TYPE_BLANK,
} from 'constants/theme.constant'
import useAuth from 'utils/hooks/useAuth'
import useDirection from 'utils/hooks/useDirection'
import useLocale from 'utils/hooks/useLocale'
import { setFirebaseUserOffline, setFirebaseUserOnline } from 'services/AuthService'
import LiveVisitors from 'components/analytics/LiveVisitors'
import { CommandPalette } from 'components/shared'
import features from 'configs/features'

const layouts = {
    [LAYOUT_TYPE_CLASSIC]: lazy(() => import('./ClassicLayout')),
    [LAYOUT_TYPE_MODERN]: lazy(() => import('./ModernLayout')),
    [LAYOUT_TYPE_STACKED_SIDE]: lazy(() => import('./StackedSideLayout')),
    [LAYOUT_TYPE_SIMPLE]: lazy(() => import('./SimpleLayout')),
    [LAYOUT_TYPE_DECKED]: lazy(() => import('./DeckedLayout')),
    [LAYOUT_TYPE_BLANK]: lazy(() => import('./BlankLayout')),
}

const Layout = () => {
    const layoutType = useSelector((state) => state.theme.layout.type)
    const currentUser = useSelector((state) => state.auth?.user)

    const { authenticated } = useAuth()

    useDirection()

    useLocale()

    // Browser visibility detection to set Firebase user offline when app is closed/hidden
    useEffect(() => {
        if (!authenticated || !currentUser) return;

        const handleVisibilityChange = async () => {
            console.log('🔍 [LAYOUT] Visibility change detected. document.hidden:', document.hidden, 'visibilityState:', document.visibilityState);

            if (document.hidden) {
                console.log('🔍 [LAYOUT] Dashboard tab hidden/closed, setting Firebase user offline');
                try {
                    await setFirebaseUserOffline(currentUser);
                    console.log('✅ [LAYOUT] Firebase user set as offline due to tab visibility change');
                } catch (error) {
                    console.log('❌ [LAYOUT] Error setting Firebase user offline on visibility change:', error);
                }
            } else {
                console.log('🔍 [LAYOUT] Dashboard tab visible again, setting Firebase user online');
                try {
                    await setFirebaseUserOnline(currentUser);
                    console.log('✅ [LAYOUT] Firebase user set as online due to tab becoming visible');
                } catch (error) {
                    console.log('❌ [LAYOUT] Error setting Firebase user online on visibility change:', error);
                }
            }
        };

        const handleBeforeUnload = async () => {
            console.log('🔍 [LAYOUT] Dashboard page unloading, setting Firebase user offline');
            try {
                // Use synchronous method for beforeunload
                await setFirebaseUserOffline(currentUser);
                console.log('✅ [LAYOUT] Firebase user set as offline due to page unload');
            } catch (error) {
                console.log('❌ [LAYOUT] Error setting Firebase user offline on page unload:', error);
            }
        };

        const handlePageHide = async () => {
            console.log('🔍 [LAYOUT] Dashboard page hidden, setting Firebase user offline');
            try {
                await setFirebaseUserOffline(currentUser);
                console.log('✅ [LAYOUT] Firebase user set as offline due to page hide');
            } catch (error) {
                console.log('❌ [LAYOUT] Error setting Firebase user offline on page hide:', error);
            }
        };

        const handleWindowFocus = async () => {
            console.log('🔍 [LAYOUT] Dashboard window focused, ensuring Firebase user is online');
            try {
                await setFirebaseUserOnline(currentUser);
                console.log('✅ [LAYOUT] Firebase user set as online due to window focus');
            } catch (error) {
                console.log('❌ [LAYOUT] Error setting Firebase user online on window focus:', error);
            }
        };

        const handleWindowBlur = async () => {
            console.log('🔍 [LAYOUT] Dashboard window blurred, setting Firebase user offline');
            try {
                await setFirebaseUserOffline(currentUser);
                console.log('✅ [LAYOUT] Firebase user set as offline due to window blur');
            } catch (error) {
                console.log('❌ [LAYOUT] Error setting Firebase user offline on window blur:', error);
            }
        };

        // Add event listeners
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('pagehide', handlePageHide);
        window.addEventListener('focus', handleWindowFocus);
        window.addEventListener('blur', handleWindowBlur);

        // Cleanup event listeners
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('pagehide', handlePageHide);
            window.removeEventListener('focus', handleWindowFocus);
            window.removeEventListener('blur', handleWindowBlur);
        };
    }, [authenticated, currentUser]);

    const AppLayout = useMemo(() => {
        if (authenticated) {
            return layouts[layoutType]
        }
        return lazy(() => import('./AuthLayout'))
    }, [layoutType, authenticated])

    return (
        <Suspense
            fallback={
                <div className="flex flex-auto flex-col h-[100vh]">
                    <Loading loading={true} />
                </div>
            }
        >
            <AppLayout />
            {authenticated && currentUser && <LiveVisitors />}
            {/* Mounted for every nav mode (not just NEW_NAV): the header
                search icon now opens the palette, so it must exist wherever
                that icon renders (audit D6). */}
            {authenticated && currentUser && <CommandPalette />}
        </Suspense>
    )
}

export default memo(Layout)
